import moment from 'moment';

/**
 * Determines if a trip should be classified as a ghost trip.
 * A trip is a ghost trip if:
 * 1. It's targeted/accepted as a ghost trip, AND
 * 2. The ghost leg departure time is NOT more than 15 minutes in the past
 *
 * @param {Object} trip - The trip object
 * @returns {boolean} - True if the trip is a ghost trip
 */
export const isGhostTrip = (trip) => {
  if (!trip) return false;

  // Check if trip is targeted/accepted as ghost trip
  const isTargetedAsGhost = trip.driver ? trip.acceptedAsGhostTrip : trip.targetedForGhostTrip;

  if (!isTargetedAsGhost) return false;

  // If no ghost leg departure time is set, consider it not a ghost trip
  if (!trip.ghostLegDepartureTime) return false;

  // Check if ghost leg departure time is more than 15 minutes in the past
  const ghostLegTime = moment(trip.ghostLegDepartureTime);
  const fourtyFiveMinutesAgo = moment().subtract(45, 'minutes');

  // If ghost leg departure is more than 45 minutes ago, it's NOT a ghost trip anymore
  if (!trip.driver && ghostLegTime.isBefore(fourtyFiveMinutesAgo)) {
    return false;
  }

  return true;
};

/**
 * Applies the daily-trips toolbar filters (cities + ghost/pending toggles, and
 * optionally a single display status). Shared by TripsDaily (the table/list) and the
 * status rail (its per-status counts) so both always reflect the same set.
 *
 * `displayStatus` is deliberately separate from the rest: the status filter's own
 * option counts have to be computed with cities/ghost/pending applied but status
 * NOT applied, so callers computing counts simply omit it.
 *
 * @param {Array} data - the loaded daily trips
 * @param {Object} filters - { citiesIds, ghostTripsOnly, pendingReassignmentOnly, displayStatus }
 * @returns {Array} the filtered trips
 */
export const filterDailyTrips = (
  data = [],
  { citiesIds = [], ghostTripsOnly = false, pendingReassignmentOnly = false, displayStatus = null } = {},
) => {
  let filtered =
    citiesIds.length > 0
      ? data.filter((t) => citiesIds.includes(t.originCity.id) || citiesIds.includes(t.destinationCity.id))
      : data;

  if (displayStatus) {
    filtered = filtered.filter((t) => t.displayStatus === displayStatus);
  }

  const predicates = [];
  if (ghostTripsOnly) predicates.push((t) => isGhostTrip(t));
  if (pendingReassignmentOnly) predicates.push((t) => t.confirmationStatus === 'PENDING_REASSIGNMENT');

  if (predicates.length > 0) {
    filtered = filtered.filter((t) => predicates.some((p) => p(t)));
  }

  return filtered;
};

/**
 * The trip paired with this one as the other half of a round trip, or null.
 *
 * Returns `{ trip, relation }` where relation is:
 *   'next'     — this is the outbound and `trip` is its return leg
 *   'previous' — this IS the return leg and `trip` is the outbound it came from
 *
 * The pairing rules match the rows TripsTable builds, including the one
 * non-obvious asymmetry: an outbound always shows its return leg, but a return
 * leg only shows its outbound when the two are in DIFFERENT status groups —
 * within one group the outbound already has its own row a few lines up, so
 * showing it again would just be the same trip twice in a row.
 *
 * (TripsTable still derives this inline because it has to emit a flat, ordered
 * row list rather than answer per-trip; if that ever gets refactored it should
 * come through here so the two can't disagree about what's paired.)
 */
export const getPairedLeg = (trip, data = []) => {
  if (!trip) return null;

  const returnLeg = data.find((t) => t.tripType === 'return' && t.previousTrip === trip.id);
  if (returnLeg) return { trip: returnLeg, relation: 'next' };

  if (trip.tripType === 'return' && trip.previousTrip) {
    const outbound = data.find((t) => t.id === trip.previousTrip);
    if (outbound && outbound.displayStatus !== trip.displayStatus) {
      return { trip: outbound, relation: 'previous' };
    }
  }

  return null;
};

/**
 * True when `trip` is a return leg whose driver is also the driver on the
 * outbound it pairs with, whatever that outbound's status. Cancelling that
 * driver from the return can strand the riders they took out, so the cancel
 * flow warns first.
 *
 * `outboundTrip` must be this return's own outbound (matched by either side of
 * the pairing, since the list data carries both `previousTrip` and `nextTrip`);
 * anything else, or a missing outbound, is treated as "no warning".
 */
export const isDriverOnPairedOutbound = (trip, outboundTrip) => {
  if (!trip?.driver || !outboundTrip?.driver) return false;
  if (trip.tripType !== 'return' && !trip.previousTrip) return false;

  const isPaired = outboundTrip.id === trip.previousTrip || outboundTrip.nextTrip === trip.id;
  if (!isPaired) return false;

  return outboundTrip.driver.id === trip.driver.id;
};
